import React, { useEffect, useState } from 'react';
import OrderContext from '../../context/OrderContext';

/** BOM part numbers are stored with a 3 character prefix that is not shown in the UI. */
const formatPartNumber = (partNumber) => {
    if (!partNumber) return 'N/A';
    return partNumber.length >= 3 ? partNumber.substring(3) : partNumber;
};

const BomPartsArchive = () => {
    const { fetchArchivedBomParts, updateArchiveBomPart } = OrderContext();

    // Data
    const [data, setData] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Pagination
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    // Sorting
    const [sortCol, setSortCol] = useState('');
    const [sortDir, setSortDir] = useState('ascending');

    // Draft text states — controlled inputs, no API trigger
    const [search, setSearch] = useState('');
    const [prcpart, setPrcpart] = useState('');
    const [topPart, setTopPart] = useState('');
    const [orderNum, setOrderNum] = useState('');

    // Applied text state — only updates on Search button / Enter
    const [applied, setApplied] = useState({ search: '', prcpart: '', topPart: '', orderNum: '' });

    const [showFilters, setShowFilters] = useState(false);

    // Unarchive
    const [unarchivingIds, setUnarchivingIds] = useState(new Set());
    const [toast, setToast] = useState(null); // { type: 'success'|'error', message }

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 3500);
        return () => clearTimeout(t);
    }, [toast]);

    const handleUnarchive = async (bomPart) => {
        const bomPartId = bomPart?.id;
        if (!bomPartId) return;
        setUnarchivingIds(prev => new Set(prev).add(bomPartId));
        const result = await updateArchiveBomPart(bomPartId, false);
        setUnarchivingIds(prev => { const s = new Set(prev); s.delete(bomPartId); return s; });
        if (result?.success) {
            setToast({ type: 'success', message: `BOM part ${formatPartNumber(bomPart.prcpart)} unarchived successfully.` });
            setData(prev => prev.filter(b => b.id !== bomPartId));
            setTotalElements(prev => Math.max(0, prev - 1));
        } else {
            setToast({ type: 'error', message: result?.message || `Failed to unarchive BOM part ${formatPartNumber(bomPart.prcpart)}.` });
        }
    };

    const hasActiveFilters = applied.search || applied.prcpart || applied.topPart || applied.orderNum;

    // API fetch — triggered by applied text filters, pagination, sort
    useEffect(() => {
        const run = async () => {
            setLoading(true);
            setError(null);
            const resp = await fetchArchivedBomParts(
                (page - 1) * perPage, perPage,
                applied.search, sortCol, sortDir,
                applied.prcpart, applied.topPart, applied.orderNum
            );
            if (resp) {
                setData(resp.content || []);
                setTotalElements(resp.totalElements || 0);
                setTotalPages(resp.totalPages || 0);
            } else {
                setError('Failed to load archived BOM parts. Please try again.');
            }
            setLoading(false);
        };
        run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, sortCol, sortDir, applied]);

    // Commit draft text values → triggers the useEffect above
    const applySearch = () => {
        setPage(1);
        setApplied({ search, prcpart, topPart, orderNum });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') applySearch();
    };

    const clearFilters = () => {
        setSearch(''); setPrcpart(''); setTopPart(''); setOrderNum('');
        setSortCol(''); setSortDir('ascending');
        setPage(1);
        setApplied({ search: '', prcpart: '', topPart: '', orderNum: '' });
    };

    const handleSort = (col) => {
        if (sortCol === col) {
            setSortDir(d => d === 'ascending' ? 'descending' : 'ascending');
        } else {
            setSortCol(col);
            setSortDir('ascending');
        }
        setPage(1);
    };

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <i className="fa fa-sort text-muted ms-1" style={{ fontSize: '11px' }} />;
        return sortDir === 'ascending' ? <i className="fa fa-sort-up ms-1" /> : <i className="fa fa-sort-down ms-1" />;
    };

    const startIdx = (page - 1) * perPage;

    return (
        <div className="box-body b-white rounded-2">
            {/* ── Header ── */}
            <div className="d-flex align-items-center justify-content-between p-2 border-bottom">
                <div className="d-flex align-items-center gap-2">
                    <i className="fa fa-archive text-secondary" style={{ fontSize: '18px' }} />
                    <h5 className="m-0">BOM Parts Archive</h5>
                    {!loading && totalElements > 0 && (
                        <span className="badge bg-secondary rounded-pill">{totalElements.toLocaleString()} records</span>
                    )}
                </div>
                <div className="d-flex gap-2">
                    {hasActiveFilters && (
                        <button className="btn btn-sm btn-outline-danger" onClick={clearFilters}>
                            <i className="fa fa-times me-1" />Clear Filters
                        </button>
                    )}
                    <button
                        className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setShowFilters(v => !v)}
                    >
                        <i className="fa fa-sliders me-1" />
                        Advanced Filters
                        {hasActiveFilters && <span className="ms-1 badge bg-danger rounded-pill" style={{ fontSize: '10px' }}>!</span>}
                    </button>
                </div>
            </div>

            {/* ── Advanced Filter Panel ── */}
            {showFilters && (
                <div className="border-bottom p-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="row g-2">
                        <div className="col-md-3">
                            <label className="form-label small fw-semibold mb-1">Component Part #</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="Component part number"
                                value={prcpart}
                                onChange={e => setPrcpart(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small fw-semibold mb-1">Top Level Part #</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="Top level part number"
                                value={topPart}
                                onChange={e => setTopPart(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small fw-semibold mb-1">Order #</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="Exact order number"
                                value={orderNum}
                                onChange={e => setOrderNum(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-3 d-flex align-items-end gap-1">
                            <button className="btn btn-sm btn-primary flex-grow-1" onClick={applySearch}>
                                <i className="fa fa-search me-1" />Search
                            </button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters} title="Reset all">
                                <i className="fa fa-refresh" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Toolbar ── */}
            <div className="d-flex align-items-center justify-content-between px-2 py-2">
                <div className="d-flex align-items-center gap-2">
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 'auto' }}
                        value={perPage}
                        onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                    >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    <span className="text-muted small">per page</span>
                </div>
                <div className="d-flex gap-1" style={{ width: '380px' }}>
                    <div className="input-group input-group-sm flex-grow-1">
                        <span className="input-group-text bg-white">
                            <i className="fa fa-search text-muted" />
                        </span>
                        <input
                            type="text"
                            className="form-control border-start-0"
                            placeholder="Search part, customer part, description, comments..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        {search && (
                            <button className="btn btn-outline-secondary" onClick={() => { setSearch(''); setApplied(a => ({ ...a, search: '' })); }}>
                                <i className="fa fa-times" />
                            </button>
                        )}
                    </div>
                    <button className="btn btn-sm btn-primary px-3" onClick={applySearch}>
                        Search
                    </button>
                </div>
            </div>

            {/* ── Error ── */}
            {error && (
                <div className="mx-2 mb-2 alert alert-danger py-2 d-flex align-items-center gap-2">
                    <i className="fa fa-exclamation-triangle" />
                    {error}
                    <button className="btn btn-sm btn-outline-danger ms-auto" onClick={applySearch}>Retry</button>
                </div>
            )}

            {/* ── Toast ── */}
            {toast && (
                <div className={`mx-2 mb-2 alert alert-${toast.type === 'success' ? 'success' : 'danger'} py-2 d-flex align-items-center gap-2`}>
                    <i className={`fa ${toast.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`} />
                    {toast.message}
                    <button type="button" className="btn-close ms-auto" style={{ fontSize: '12px' }} onClick={() => setToast(null)} />
                </div>
            )}

            {/* ── Table ── */}
            <div className="px-2">
                <div className="table-responsive bridge-manual-scroll" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    <table className="table table-bordered table-hover table-sm mb-0">
                        <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr>
                                <th className="text-center" style={{ minWidth: '55px', width: '55px' }}>S.No</th>
                                <th className="text-center" style={{ minWidth: '140px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('prcpart')}>
                                    Component Part <SortIcon col="prcpart" />
                                </th>
                                <th className="text-center" style={{ minWidth: '200px' }}>Description</th>
                                <th className="text-center" style={{ minWidth: '150px' }}>Top Level Part</th>
                                <th className="text-center" style={{ minWidth: '150px' }}>Used In Orders</th>
                                <th className="text-center" style={{ minWidth: '90px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('qty_per_top')}>
                                    Qty/Top <SortIcon col="qty_per_top" />
                                </th>
                                <th className="text-center" style={{ minWidth: '70px' }}>QOH</th>
                                <th className="text-center" style={{ minWidth: '70px' }}>QOO</th>
                                <th className="text-center" style={{ minWidth: '100px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('unit_cost')}>
                                    Unit Cost <SortIcon col="unit_cost" />
                                </th>
                                <th className="text-center" style={{ minWidth: '180px' }}>Comments</th>
                                <th className="text-center" style={{ minWidth: '110px', width: '110px' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="11" className="text-center py-5">
                                        <div className="spinner-border text-secondary spinner-border-sm me-2" role="status" />
                                        <span className="text-muted">Loading archived BOM parts...</span>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan="11" className="text-center text-muted py-5">
                                        <i className="fa fa-archive fa-2x mb-2 d-block" style={{ color: '#dee2e6' }} />
                                        {hasActiveFilters ? 'No archived BOM parts match the current filters.' : 'No archived BOM parts found.'}
                                    </td>
                                </tr>
                            ) : (
                                data.map((bomPart, i) => {
                                    const part = bomPart.part || {};
                                    const busy = unarchivingIds.has(bomPart.id);
                                    const topParts = bomPart.topParts || [];
                                    const orderNums = bomPart.orderNums || [];
                                    return (
                                        <tr key={`arch-bom-${bomPart.id ?? i}`}>
                                            <td className="text-center align-middle">{startIdx + i + 1}</td>
                                            <td className="text-center align-middle">
                                                {part.id ? (
                                                    <a href={`https://gigaflightinc.cetecerp.com/part/${part.id}/view`} target="_blank" rel="noopener noreferrer"
                                                        title={bomPart.prcpart || ''} style={{ textDecoration: 'none', fontWeight: 500 }}>
                                                        {formatPartNumber(bomPart.prcpart)}
                                                    </a>
                                                ) : (
                                                    <span title={bomPart.prcpart || ''}>{formatPartNumber(bomPart.prcpart)}</span>
                                                )}
                                            </td>
                                            <td className="text-center align-middle">
                                                <div title={part.description || ''}
                                                    style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {part.description || '-'}
                                                </div>
                                            </td>
                                            <td className="text-center align-middle">
                                                {topParts.length > 0
                                                    ? topParts.map(p => formatPartNumber(p)).join(', ')
                                                    : '-'}
                                            </td>
                                            <td className="text-center align-middle">
                                                {orderNums.length > 0 ? orderNums.map((num, idx) => (
                                                    <React.Fragment key={num}>
                                                        {idx > 0 && ', '}
                                                        <a href={`https://gigaflightinc.cetecerp.com/order/MN${num}/view`} target="_blank" rel="noopener noreferrer"
                                                            style={{ textDecoration: 'none' }}>
                                                            {num}
                                                        </a>
                                                    </React.Fragment>
                                                )) : '-'}
                                            </td>
                                            <td className="text-center align-middle">{bomPart.qty_per_top ?? '-'}</td>
                                            <td className="text-center align-middle">{Number(part.qoh_cache) || 0}</td>
                                            <td className="text-center align-middle">{Number(part.qoo_cache) || 0}</td>
                                            <td className="text-center align-middle">
                                                {bomPart.unit_cost != null ? Number(bomPart.unit_cost).toFixed(2) : '-'}
                                            </td>
                                            <td className="text-center align-middle">
                                                <div title={bomPart.comments || ''}
                                                    style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {bomPart.comments || '-'}
                                                </div>
                                            </td>
                                            <td className="text-center align-middle">
                                                <button
                                                    className="btn btn-sm btn-outline-warning"
                                                    style={{ fontSize: '11px', whiteSpace: 'nowrap' }}
                                                    disabled={busy}
                                                    onClick={() => handleUnarchive(bomPart)}
                                                    title={`Unarchive BOM part ${formatPartNumber(bomPart.prcpart)}`}
                                                >
                                                    {busy
                                                        ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Restoring...</>
                                                        : <><i className="fa fa-undo me-1" />Unarchive</>
                                                    }
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Pagination ── */}
            {!loading && totalPages > 0 && (
                <div className="d-flex justify-content-between align-items-center px-2 py-2 border-top">
                    <span className="text-muted small">
                        Showing <strong>{startIdx + 1}</strong>–<strong>{Math.min(startIdx + perPage, totalElements)}</strong> of{' '}
                        <strong>{totalElements.toLocaleString()}</strong> archived BOM parts
                    </span>
                    <ul className="pagination pagination-sm mb-0 align-items-center">
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(1)} disabled={page === 1}>«</button>
                        </li>
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(p => p - 1)} disabled={page === 1}>Previous</button>
                        </li>
                        {totalPages > 1 && (
                            <li className="page-item">
                                <select className="form-select form-select-sm mx-2" style={{ width: 'auto' }} value={page} onChange={e => setPage(Number(e.target.value))}>
                                    {[...Array(totalPages)].map((_, i) => (
                                        <option key={i} value={i + 1}>Page {i + 1} of {totalPages}</option>
                                    ))}
                                </select>
                            </li>
                        )}
                        <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>Next</button>
                        </li>
                        <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
};

export default BomPartsArchive;
